import { mapFromCompanyProfile, mapToCompanyProfile } from 'mappings/companyProfileMapping';
import { mapFromCopilotDocumentIngestResult, mapToCopilotDocumentIngest } from 'mappings/copilotDocumentIngestMapping';
import {
  mapFromCopilotExternalDocumentInstructionEdit,
  mapToCopilotExternalDocumentInstructionEditRequest,
} from 'mappings/copilotExternalDocumentInstructionEditMapping';
import {
  mapFromAITaskSessionDetail,
  mapFromAITaskSessionSummary,
  mapFromCopilotFileLibrarySaveResult,
  mapFromCopilotGeneric,
  mapFromCopilotOnboardingProject,
  mapFromCopilotProcessingActivity,
  mapFromCopilotQuestionOption,
  mapFromCopilotRiskControlSuggestions,
  mapFromCopilotTaskChecklistSuggestions,
  mapFromTenantProcessStateAnnualPlan,
  mapFromTenantProcessStateAuditFindings,
  mapFromTenantProcessStateAutomationDrafting,
  mapFromTenantProcessStateKpiReport,
  mapFromTenantProcessStateEntityEvidenceAudit,
  mapFromTenantProcessStateControlDocumentationAudit,
  mapFromTenantProcessStateControlEvidenceAudit,
  mapFromActiveFrameworkAudit,
  mapFromTenantProcessStateFrameworkAudit,
  mapFromTenantProcessStateLibrary,
  mapFromTenantProcessStateProcessSuggestions,
  mapFromTenantProcessStateTaskEvidenceAudit,
  mapToCopilotConversation,
  mapToCopilotGeneric,
  mapToCopilotProcessingActivity,
  mapToCopilotRiskControlSuggestionsRequest,
  mapToCopilotTaskChecklistSuggestionsRequest,
  mapToTenantProcessState,
  mapToTenantProcessStateAnnualPlan,
  mapToTenantProcessStateAuditFindings,
  mapToTenantProcessStateAutomationDrafting,
  mapToTenantProcessStateKpiReport,
  mapToTenantProcessStateEntityEvidenceAudit,
  mapToTenantProcessStateControlDocumentationAudit,
  mapToTenantProcessStateControlEvidenceAudit,
  mapToTenantProcessStateFrameworkAudit,
  mapToTenantProcessStateLibrary,
  mapToTenantProcessStateProcessSuggestions,
  mapToTenantProcessStateTaskEvidenceAudit,
} from 'mappings/copilotMapping';
import {
  mapFromTenantProcessStateDocumentSummary,
  mapToTenantProcessStateDocumentSummary,
} from 'mappings/documentSummaryMapping';
import { mapFromTenantProcessStateInsight, mapToTenantProcessStateInsight } from 'mappings/insightMapping';
import {
  mapFromLibraryDocumentInstructionEditStartResult,
  mapFromLibraryDocumentInstructionEditStatus,
  mapFromLibraryDocumentInstructionEditTransitionResult,
} from 'mappings/libraryDocumentInstructionEditProcessMapping';
import { mapToStringValue } from 'mappings/stringValueMapping';
import { mapToOnboardingSteps } from 'mappings/tenantMapping';
import CompanyProfile from 'models/companyProfile';
import { AITaskSessionSummary } from 'models/copilot/aiTaskSession';
import { ContentBlockKind, CopilotConversation } from 'models/copilot/copilotConversation';
import { CopilotDocumentIngest, CopilotDocumentIngestResult } from 'models/copilot/copilotDocumentIngest';
import { CopilotActiveProcessContext } from 'models/copilot/copilotActiveProcessContext';
import { CopilotActiveField } from 'models/copilot/copilotField';
import { CopilotFileLibrarySaveResult } from 'models/copilot/copilotFileBlock';
import {
  CopilotExternalDocumentInstructionEdit,
  CopilotExternalDocumentInstructionEditRequest,
} from 'models/copilot/copilotExternalDocumentInstructionEdit';
import { CopilotGenericContent, CopilotSelectedDocument } from 'models/copilot/copilotGeneric';
import { CopilotOnboardingProject } from 'models/copilot/copilotOnboardingProject';
import {
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelProcessStateData,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotProcessingActivity } from 'models/copilot/copilotProcessingActivity';
import { CopilotQuestionOption, CopilotQuestionOptionFilter } from 'models/copilot/copilotQuestionOption';
import { CopilotRiskControlSuggestions } from 'models/copilot/copilotRiskControlSuggestions';
import { CopilotStringValueDTO } from 'models/copilot/copilotStringValue';
import { CopilotTaskChecklistSuggestions } from 'models/copilot/copilotTaskChecklistSuggestions';
import {
  LibraryDocumentInstructionEditRestartMode,
  LibraryDocumentInstructionEditStartResult,
  LibraryDocumentInstructionEditStatus,
  LibraryDocumentInstructionEditTransitionResult,
} from 'models/copilot/libraryDocumentInstructionEditProcess';
import { TenantProcessStateAnnualPlan } from 'models/copilot/Processes/AnnualPlan/tenantProcessStateAnnualPlan';
import {
  AuditFindingOriginal,
  TenantProcessStateAuditFindings,
} from 'models/copilot/Processes/AuditFindings/tenantProcessStateAuditFindings';
import { TenantProcessStateControlDocumentationAudit } from 'models/copilot/Processes/ControlDocumentationAudit/copilotControlDocumentationAudit';
import { TenantProcessStateControlEvidenceAudit } from 'models/copilot/Processes/ControlEvidenceAudit/copilotControlEvidenceAudit';
import {
  ActiveFrameworkAudit,
  ActiveFrameworkAuditDTO,
  TenantProcessStateFrameworkAudit,
} from 'models/copilot/Processes/FrameworkAudit/copilotFrameworkAudit';
import { TenantProcessStateLibrary } from 'models/copilot/Processes/Library/tenantProcessStateLibrary';
import {
  CopilotProcessSuggestion,
  TenantProcessStateProcessSuggestions,
} from 'models/copilot/Processes/ProcessSuggestions/copilotProcessSuggestion';
import { TenantProcessStateAutomationDrafting } from 'models/copilot/Processes/AutomationDrafting/tenantProcessStateAutomationDrafting';
import { TenantProcessStateKpiReport } from 'models/copilot/kpiReportProcessState';
import { TenantProcessStateEntityEvidenceAudit } from 'models/copilot/entityEvidenceAudit';
import { TenantProcessStateSupplierCompliance } from 'models/copilot/Processes/SupplierCompliance/tenantProcessStateSupplierCompliance';
import { TenantProcessStateSupplierIncidents } from 'models/copilot/Processes/SupplierIncidents/tenantProcessStateSupplierIncidents';
import { TenantProcessStateTaskEvidenceAudit } from 'models/copilot/Processes/TaskEvidenceAudit/copilotTaskEvidenceAudit';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import CompanyProfileDTO from 'models/dto/companyProfileDTO';
import { ControlLinkDTO } from 'models/dto/controlLinkDTO';
import {
  AITaskApprovalSubmitDTO,
  AITaskSessionDetailDTO,
  AITaskSessionSummaryDTO,
} from 'models/dto/copilot/aiTaskSessionDTO';
import { CopilotDocumentIngestResultDTO } from 'models/dto/copilot/copilotDocumentIngestDTO';
import { CopilotExternalDocumentInstructionEditDTO } from 'models/dto/copilot/copilotExternalDocumentInstructionEditDTO';
import { CopilotFileLibrarySaveResultDTO } from 'models/dto/copilot/copilotFileBlockDTO';
import { ReportSaveLocationDTO } from 'models/dto/library/reportSaveLocationDTO';
import { ReportSaveLocation, ReportSaveRequest } from 'models/library/reportSaveLocation';
import { mapFromReportSaveLocation, mapToReportSaveRequest } from 'mappings/reportSaveLocationMapping';
import { CopilotGenericContentDTO } from 'models/dto/copilot/copilotGenericDTO';
import { CopilotOnboardingProjectDTO } from 'models/dto/copilot/copilotOnboardingProjectDTO';
import { CopilotProcessingActivityDTO } from 'models/dto/copilot/copilotProcessingActivityDTO';
import { CopilotQuestionOptionDTO } from 'models/dto/copilot/copilotQuestionOptionDTO';
import { CopilotRiskControlSuggestionsDTO } from 'models/dto/copilot/copilotRiskControlSuggestionsDTO';
import { CopilotTaskChecklistSuggestionsDTO } from 'models/dto/copilot/copilotTaskChecklistSuggestionsDTO';
import { KpiReportSessionDTO, KpiReportSessionRequestDTO } from 'models/dto/copilot/kpiReportSessionDTO';
import {
  LibraryDocumentInstructionEditClaimRequestDTO,
  LibraryDocumentInstructionEditCompleteRequestDTO,
  LibraryDocumentInstructionEditFailRequestDTO,
  LibraryDocumentInstructionEditRunRequestDTO,
  LibraryDocumentInstructionEditStartRequestDTO,
  LibraryDocumentInstructionEditStartResultDTO,
  LibraryDocumentInstructionEditStatusDTO,
  LibraryDocumentInstructionEditTransitionResultDTO,
} from 'models/dto/copilot/libraryDocumentInstructionEditProcessDTO';
import { TenantProcessStateBaseDTO } from 'models/dto/copilot/tenantProcessStateBaseDTO';
import { DocumentSummary } from 'models/documentSummary';
import { EntityTypes } from 'models/entity';
import { Insight } from 'models/insight';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { PrivacyFramework } from 'models/processingActivity';
import ResourceLink from 'models/resourceLink';
import ResourceList from 'models/resourceList';
import StringValue from 'models/stringValue/stringValue';
import { StringValue_Translation } from 'models/stringValue/stringValue_Translation';
import Task from 'models/tasks/task';
import { TenantOnboardingSteps } from 'models/tenant';
import { ProgressHubStatus } from 'services/SignalR/ProgressHub';
import {
  mapFromTenantProcessStateSupplierCompliance,
  mapFromTenantProcessStateSupplierIncidents,
  mapToTenantProcessStateSupplierCompliance,
  mapToTenantProcessStateSupplierIncidents,
} from 'mappings/supplierProcessMapping';
import AppError from 'utils/appError';
import { fromApiDateTimeOptional } from 'utils/datetime';
import { sortOnNumber } from 'utils/sorting';
import http from './httpService';

const appendConnectionId = (url: string, connectionId: string | null): string => {
  if (!connectionId || connectionId === 'null' || connectionId === 'undefined') return url;

  return `${url}${url.includes('?') ? '&' : '?'}connectionId=${encodeURIComponent(connectionId)}`;
};

// Existing controls that could mitigate the saved risk, picked by the model from the ones not linked yet.
export async function apiCopilotRiskControlSuggestions(
  riskId: number,
  connectionId: string | null,
  accessToken: string,
): Promise<CopilotRiskControlSuggestions | undefined> {
  try {
    const dto = mapToCopilotRiskControlSuggestionsRequest(riskId);
    const url = appendConnectionId('copilot/suggestions/riskcontrols', connectionId);
    const ar = await http.post<CopilotRiskControlSuggestionsDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotRiskControlSuggestions(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotTaskChecklistSuggestions(
  name: string,
  description: string,
  steps: string[],
  connectionId: string | null,
  accessToken: string,
  signal?: AbortSignal,
): Promise<CopilotTaskChecklistSuggestions | undefined> {
  try {
    const dto = mapToCopilotTaskChecklistSuggestionsRequest(name, description, steps);
    const url = appendConnectionId('copilot/suggestions/taskchecklist', connectionId);
    const ar = await http.post<CopilotTaskChecklistSuggestionsDTO>(
      url,
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotTaskChecklistSuggestions(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotCreateOnboardingProject(
  onboardingSteps: TenantOnboardingSteps,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<CopilotOnboardingProject | undefined> {
  try {
    const dto = mapToOnboardingSteps(onboardingSteps);
    const ar = await http.post<CopilotOnboardingProjectDTO>(
      `copilot/onboarding/project`,
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotOnboardingProject(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotCreateOnboardingProjectDocument(
  onboardingSteps: TenantOnboardingSteps,
  connectionId: string | null,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<CopilotOnboardingProject | undefined> {
  try {
    const dto = mapToOnboardingSteps(onboardingSteps);
    const url = appendConnectionId('copilot/onboarding/project/document', connectionId);
    const ar = await http.post<CopilotOnboardingProjectDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotOnboardingProject(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotFinishOnboarding(
  onboardingSteps: TenantOnboardingSteps,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<CopilotOnboardingProject | undefined> {
  try {
    const dto = mapToOnboardingSteps(onboardingSteps);
    const ar = await http.post<CopilotOnboardingProjectDTO>(
      `copilot/onboarding/project/finish`,
      dto,
      http.getRequestConfig(accessToken),
    );

    if (!ar || !ar.data) return undefined;

    return mapFromCopilotOnboardingProject(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotFinishOnboardingProgress(accessToken: string): Promise<number | undefined> {
  try {
    const ar = await http.get<number | undefined>(
      `copilot/onboarding/project/finish/progress`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetCopilotDocumentContent(
  sessionId: number,
  blockId: string,
  accessToken: string,
): Promise<Blob> {
  try {
    const ar = await http.get<Blob>(`copilot/document/content/${sessionId}/${encodeURIComponent(blockId)}`, {
      ...http.getRequestConfig(accessToken),
      responseType: 'blob',
    });

    return ar.data;
  } catch (err) {
    // the error body is a Blob too; the status stays in AppError.code: 410 expired, 409 scan pending, 403 refused
    // (getCopilotFileUnavailableReason)
    throw await AppError.fromBlobApiError(err);
  }
}

// The default location of a File block that carries an output purpose (PLAN-069): the category, the folder and the
// file name, resolved without touching the provider; undefined (204) when the block has no purpose.
export async function apiGetCopilotFileSaveLocation(
  sessionId: number,
  blockId: string,
  accessToken: string,
): Promise<ReportSaveLocation | undefined> {
  try {
    const ar = await http.get<ReportSaveLocationDTO>(
      `copilot/document/file/${sessionId}/${encodeURIComponent(blockId)}/library/location`,
      http.getRequestConfig(accessToken),
    );
    if (ar.status === 204 || !ar.data) return undefined;

    return mapFromReportSaveLocation(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Saves a File block's bytes as a new document in the library: the chosen category and folder, or the default
// location of the purpose; the block's payload then names it. A gone start folder of the default answers
// OUTPUTLOC0009 in AppError.data.code.
export async function apiSaveCopilotFileToLibrary(
  sessionId: number,
  blockId: string,
  request: ReportSaveRequest,
  accessToken: string,
): Promise<CopilotFileLibrarySaveResult> {
  try {
    const ar = await http.post<CopilotFileLibrarySaveResultDTO>(
      `copilot/document/file/${sessionId}/${encodeURIComponent(blockId)}/library`,
      mapToReportSaveRequest(request),
      http.getRequestConfig(accessToken),
    );

    return mapFromCopilotFileLibrarySaveResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetCopilotDocumentPreviewUrl(
  sessionId: number,
  blockId: string,
  accessToken: string,
): Promise<string> {
  try {
    const ar = await http.get<string>(
      `copilot/document/content/${sessionId}/${encodeURIComponent(blockId)}/previewurl`,
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetCopilotDocumentArrayBuffer(
  sessionId: number,
  blockId: string,
  accessToken: string,
): Promise<ArrayBuffer> {
  try {
    const ar = await http.get<ArrayBuffer>(`copilot/document/content/${sessionId}/${encodeURIComponent(blockId)}`, {
      ...http.getRequestConfig(accessToken),
      responseType: 'arraybuffer',
    });

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotBuildCompanyProfile(accessToken: string): Promise<CompanyProfile | undefined> {
  try {
    const ar = await http.post<CompanyProfileDTO>(
      `copilot/tenant/companyProfile/build`,
      null,
      http.getRequestConfig(accessToken),
    );

    return mapFromCompanyProfile(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotDraftCompanyProfile(
  accessToken: string,
  profile: CompanyProfile,
): Promise<CompanyProfile | undefined> {
  try {
    const ar = await http.post<CompanyProfileDTO>(
      `copilot/tenant/companyProfile/draft`,
      mapToCompanyProfile(profile),
      http.getRequestConfig(accessToken),
    );

    return mapFromCompanyProfile(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotExecuteProcessAnnualPlan(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  tasks: Task[],
  connectionId: string | null,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<TenantProcessStateAnnualPlan | undefined> {
  try {
    const dto = mapToTenantProcessStateAnnualPlan(conversation, status, tasks);
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateAnnualPlan(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotExecuteProcessAuditFindings(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  internalAuditId: number,
  newTasks: Task[],
  updatedTasks: Task[],
  originals: AuditFindingOriginal[],
  connectionId: string | null,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<TenantProcessStateAuditFindings | undefined> {
  try {
    const dto = mapToTenantProcessStateAuditFindings(
      conversation,
      status,
      internalAuditId,
      newTasks,
      updatedTasks,
      originals,
    );
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateAuditFindings(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotExecuteProcessLibrary(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  links: ResourceLink[],
  lists: ResourceList[],
  controlLinks: ControlLinkDTO[],
  connectionId: string | null,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<TenantProcessStateLibrary | undefined> {
  try {
    const dto = mapToTenantProcessStateLibrary(conversation, links, lists, controlLinks, status);
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateLibrary(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotExecuteProcessSuggestions(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  processes: CopilotProcessSuggestion[],
  currentProcesses: CopilotProcessSuggestion[],
  connectionId: string | null,
  accessToken: string,
): Promise<TenantProcessStateProcessSuggestions | undefined> {
  try {
    const dto = mapToTenantProcessStateProcessSuggestions(conversation, status, processes, currentProcesses);
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateProcessSuggestions(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotExecuteProcessControlEvidenceAudit(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  controlId: number,
  createdTaskIds: number[],
  connectionId: string | null,
  accessToken: string,
  periodStart?: string,
  periodEnd?: string,
  normIds?: number[],
): Promise<TenantProcessStateControlEvidenceAudit | undefined> {
  try {
    const dto = mapToTenantProcessStateControlEvidenceAudit(
      conversation,
      status,
      controlId,
      createdTaskIds,
      periodStart,
      periodEnd,
      normIds,
    );
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateControlEvidenceAudit(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotExecuteProcessControlDocumentationAudit(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  controlId: number,
  createdTaskIds: number[],
  connectionId: string | null,
  accessToken: string,
  periodStart?: string,
  periodEnd?: string,
  normIds?: number[],
): Promise<TenantProcessStateControlDocumentationAudit | undefined> {
  try {
    const dto = mapToTenantProcessStateControlDocumentationAudit(
      conversation,
      status,
      controlId,
      createdTaskIds,
      periodStart,
      periodEnd,
      normIds,
    );
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateControlDocumentationAudit(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotExecuteProcessFrameworkAudit(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  isoNormId: number,
  includeDocumentation: boolean,
  includeEvidence: boolean,
  sendCompletionEmail: boolean,
  connectionId: string | null,
  accessToken: string,
): Promise<TenantProcessStateFrameworkAudit | undefined> {
  try {
    const dto = mapToTenantProcessStateFrameworkAudit(
      conversation,
      status,
      isoNormId,
      includeDocumentation,
      includeEvidence,
      sendCompletionEmail,
    );
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateFrameworkAudit(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// The rows a lookup question offers (a QuestionSet block's question of kind lookup). A 400 means the card sent a source
// or filter the server refuses, a 403 a source the user may not read; both reach the caller as an AppError.
export async function apiCopilotGetQuestionOptions(
  source: string,
  filter: CopilotQuestionOptionFilter | undefined,
  search: string | undefined,
  max: number,
  accessToken: string,
): Promise<CopilotQuestionOption[]> {
  try {
    const ar = await http.get<CopilotQuestionOptionDTO[]>('copilot/questions/options', {
      ...http.getRequestConfig(accessToken),
      params: {
        source,
        search: search || undefined,
        max,
        tagCategory: filter?.tagCategory ?? undefined,
        stringValueFamily: filter?.stringValueFamily ?? undefined,
        classificationGroup: filter?.classificationGroup ?? undefined,
        nameContains: filter?.nameContains || undefined,
      },
    });
    if (!ar || !ar.data) return [];

    return ar.data.map((dto) => mapFromCopilotQuestionOption(dto));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// The framework audits running right now, for the status markers outside the copilot panel.
export async function apiCopilotGetActiveFrameworkAudits(accessToken: string): Promise<ActiveFrameworkAudit[]> {
  try {
    const ar = await http.get<ActiveFrameworkAuditDTO[]>(
      'copilot/process/frameworkaudit/active',
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return [];

    return ar.data.map((dto) => mapFromActiveFrameworkAudit(dto));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotExecuteProcessTaskEvidenceAudit(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  taskId: number,
  connectionId: string | null,
  accessToken: string,
): Promise<TenantProcessStateTaskEvidenceAudit | undefined> {
  try {
    const dto = mapToTenantProcessStateTaskEvidenceAudit(conversation, status, taskId);
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateTaskEvidenceAudit(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// One execute of the AutomationDrafting process (phase 27): the conversation with the answered cards and, for a stage
// action, the command (accept, test, select:<id|fresh>, schedule:<choice>). The answer carries the run status so the
// definition can end the process when the server completed or failed it in this request.
export async function apiCopilotExecuteProcessAutomationDrafting(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  entityType: number,
  entityId: number,
  command: string | undefined,
  connectionId: string | null,
  accessToken: string,
): Promise<TenantProcessStateAutomationDrafting | undefined> {
  try {
    const dto = mapToTenantProcessStateAutomationDrafting(conversation, status, entityType, entityId, command);
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateAutomationDrafting(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// One execute of the KPI report process (PLAN-070): the start with the job or the KPIs the surface knew, or the
// answer to the step-1 card. The row is keyed on the job; before the job exists the reservation id names it.
export async function apiCopilotExecuteProcessKpiReport(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  jobId: number | undefined,
  kpiIds: number[] | undefined,
  reservationId: string | undefined,
  contextKey: string | undefined,
  format: number | undefined,
  connectionId: string | null,
  accessToken: string,
): Promise<TenantProcessStateKpiReport | undefined> {
  try {
    const dto = mapToTenantProcessStateKpiReport(
      conversation,
      status,
      jobId,
      kpiIds,
      reservationId,
      contextKey,
      format,
    );
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateKpiReport(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// One execute of the entity evidence audit (PLAN-077): the start, keyed on the entity, with Force for Run again. The
// answer is the row after the prepare: in progress while the judge runs in the background, completed on a cache hit.
export async function apiCopilotExecuteProcessEntityEvidenceAudit(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  entityType: EntityTypes,
  entityId: number,
  force: boolean,
  connectionId: string | null,
  accessToken: string,
): Promise<TenantProcessStateEntityEvidenceAudit | undefined> {
  try {
    const dto = mapToTenantProcessStateEntityEvidenceAudit(conversation, status, entityType, entityId, force);
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateEntityEvidenceAudit(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * One execute of the incidents investigation of a Supplier asset (PLAN-073): the start, a typed question, the card's
 * "Create incidents" (command createIncidents) or the save (Finish with command save). The row is the asset's.
 */
export async function apiCopilotExecuteProcessSupplierIncidents(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  assetId: number,
  consumerTaskId: number | undefined,
  command: string | undefined,
  connectionId: string | null,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<TenantProcessStateSupplierIncidents | undefined> {
  try {
    const dto = mapToTenantProcessStateSupplierIncidents(conversation, status, assetId, consumerTaskId, command);
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateSupplierIncidents(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * One execute of the published-compliance investigation of a Supplier asset (PLAN-073): the start (with the assessment
 * task when an assessment started it), a typed question, or a card's command.
 */
export async function apiCopilotExecuteProcessSupplierCompliance(
  status: TenantProcessStatus,
  conversation: CopilotConversation,
  assetId: number,
  consumerTaskId: number | undefined,
  command: string | undefined,
  connectionId: string | null,
  accessToken: string,
): Promise<TenantProcessStateSupplierCompliance | undefined> {
  try {
    const dto = mapToTenantProcessStateSupplierCompliance(conversation, status, assetId, consumerTaskId, command);
    const url = appendConnectionId('copilot/process/execute', connectionId);
    const ar = await http.post<TenantProcessStateBaseDTO>(url, dto, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromTenantProcessStateSupplierCompliance(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The Copilot session of the caller's finished KPI report (PLAN-076): made once from the report's thread as a completed
 * session under the panel's context key, so the File card reaches its file; a second call returns the same id.
 */
export async function apiCopilotEnsureKpiReportSession(
  jobId: number,
  contextKey: string,
  accessToken: string,
): Promise<number> {
  try {
    const ar = await http.post<KpiReportSessionDTO>(
      `copilot/process/kpireport/${jobId}/session`,
      new KpiReportSessionRequestDTO(contextKey),
      http.getRequestConfig(accessToken),
    );

    return ar.data.sessionId;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Runs the Insight process for one entity and returns the answer in the reader's language.
 *
 * No connection id: the card waits for the row it asked for rather than following a progress hub,
 * and the process answers from its own cache when the state has not moved.
 */
export async function apiCopilotExecuteProcessInsight(
  entityType: EntityTypes,
  entityId: number,
  focus: string,
  force: boolean,
  lang: string,
  accessToken: string,
): Promise<Insight | undefined> {
  try {
    const dto = mapToTenantProcessStateInsight(entityType, entityId, focus, force);
    const ar = await http.post<TenantProcessStateBaseDTO>(
      'copilot/process/execute',
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    const insights = mapFromTenantProcessStateInsight(ar.data).insights;
    // The server keys the entry on its own reading of the user language; fall back to the one entry
    // for this focus when the client's language word differs (e.g. right after a language change).

    return (
      insights.get(`${focus}:${lang}`) ??
      Array.from(insights.entries()).find(([key]) => key.startsWith(`${focus}:`))?.[1]
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Runs the DocumentSummary process for one document and returns its two lines in the reader's
 * language.
 *
 * No connection id: the room asks per document and waits for the row it asked for, and the process
 * answers from its own cache while the document version has not moved. The optional signal is what
 * Stop pulls: the document already with the model finishes server-side, this call simply stops
 * waiting for it.
 */
export async function apiCopilotExecuteProcessDocumentSummary(
  linkId: number,
  force: boolean,
  lang: string,
  accessToken: string,
  signal?: AbortSignal,
): Promise<DocumentSummary | undefined> {
  try {
    const dto = mapToTenantProcessStateDocumentSummary(linkId, force);
    const ar = await http.post<TenantProcessStateBaseDTO>(
      'copilot/process/execute',
      dto,
      http.getRequestConfig(accessToken, undefined, undefined, signal),
    );
    if (!ar || !ar.data) return undefined;

    const summaries = mapFromTenantProcessStateDocumentSummary(ar.data).summaries;
    // The server keys the entry on its own reading of the user language; fall back to the single
    // entry when the client's language word differs (e.g. right after a language change).

    return summaries.get(lang) ?? Array.from(summaries.values())[0];
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The lines already stored for one document, without running anything. This is what makes a reload
 * free: the room reads what the process wrote before, and only asks for new lines on request.
 */
export async function apiCopilotGetDocumentSummary(
  linkId: number,
  lang: string,
  accessToken: string,
): Promise<DocumentSummary | undefined> {
  try {
    const dto = mapToTenantProcessState(TenantProcess.DocumentSummary, EntityTypes.Link, linkId);
    const ar = await http.post<TenantProcessStateBaseDTO | null>(
      `copilot/process/state`,
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    const summaries = mapFromTenantProcessStateDocumentSummary(ar.data).summaries;

    return summaries.get(lang) ?? Array.from(summaries.values())[0];
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotGetProcessStatus(
  processId: TenantProcess,
  typeOfEntity: number,
  entityId: number,
  accessToken: string,
): Promise<TenantProcessStatus> {
  try {
    const dto = mapToTenantProcessState(processId, typeOfEntity, entityId);
    const ar = await http.post<TenantProcessStatus>(
      `copilot/process/state/status`,
      dto,
      http.getRequestConfig(accessToken),
    );

    return ar.data || TenantProcessStatus.NotStarted;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Latest persisted progress of a long-running process, used to catch up a progress bar after a
// reload/reconnect before live ProgressHub updates resume, and polled as the completion signal of a
// background-native run. The server serves the status as ProgressHubStatus (the enum of the live hub
// pushes) and, once a run has ended without ever reporting progress, a terminal snapshot derived from
// the run status. Returns null while there is no snapshot and the run has not ended.
export interface CopilotProcessProgress {
  percentage: number;
  processedItems: number;
  totalItems: number;
  currentStep?: string;
  status: ProgressHubStatus;
}

export async function apiCopilotGetProcessProgress(
  processId: TenantProcess,
  typeOfEntity: number,
  entityId: number,
  accessToken: string,
): Promise<CopilotProcessProgress | null> {
  try {
    const dto = mapToTenantProcessState(processId, typeOfEntity, entityId);
    //the process belongs to the partner's own tenant, and this is polled every 5 seconds with a silent catch, so
    //it must never inherit a customer scope: the poll would 403 forever and never reach a terminal state
    const ar = await http.post<CopilotProcessProgress | null>(
      `copilot/process/state/progress`,
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );

    //a 204 arrives as an empty string, which `??` would hand back as a snapshot
    return ar.data || null;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// Full persisted state (run status + conversation) of an AI process. Read-only: unlike execute it does
// not run a lifecycle step. Used to load the result of a background-native run (control audits) once its
// progress reports Completed, and to catch up after a reload. Returns null when there is no persisted state.
const processStateConversationMappers: Partial<
  Record<TenantProcess, (dto: TenantProcessStateBaseDTO, cache: GlobalDataCache) => CopilotPanelProcessStateData>
> = {
  [TenantProcess.AnnualPlan]: mapFromTenantProcessStateAnnualPlan,
  [TenantProcess.AuditFindings]: mapFromTenantProcessStateAuditFindings,
  [TenantProcess.Library]: mapFromTenantProcessStateLibrary,
  [TenantProcess.ProcessSuggestions]: mapFromTenantProcessStateProcessSuggestions,
  [TenantProcess.ControlEvidenceAudit]: (dto) => mapFromTenantProcessStateControlEvidenceAudit(dto),
  [TenantProcess.ControlDocumentationAudit]: (dto) => mapFromTenantProcessStateControlDocumentationAudit(dto),
  [TenantProcess.TaskEvidenceAudit]: (dto) => mapFromTenantProcessStateTaskEvidenceAudit(dto),
  [TenantProcess.FrameworkAudit]: (dto) => mapFromTenantProcessStateFrameworkAudit(dto),
  [TenantProcess.AutomationDrafting]: (dto) => mapFromTenantProcessStateAutomationDrafting(dto),
  [TenantProcess.KpiReport]: (dto) => mapFromTenantProcessStateKpiReport(dto),
  [TenantProcess.SupplierIncidents]: mapFromTenantProcessStateSupplierIncidents,
  [TenantProcess.SupplierCompliance]: (dto) => mapFromTenantProcessStateSupplierCompliance(dto),
  [TenantProcess.EntityEvidenceAudit]: (dto) => mapFromTenantProcessStateEntityEvidenceAudit(dto),
};

export async function apiCopilotGetProcessState(
  processId: TenantProcess,
  typeOfEntity: number,
  entityId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<CopilotPanelProcessState | null> {
  try {
    const dto = mapToTenantProcessState(processId, typeOfEntity, entityId);
    const ar = await http.post<TenantProcessStateBaseDTO | null>(
      `copilot/process/state`,
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return null;

    const mapProcessState = processStateConversationMappers[processId];
    if (!mapProcessState) {
      throw new Error(`Unsupported Copilot process state mapper: ${processId}`);
    }

    const state = mapProcessState(ar.data, globalDataCache);

    return {
      processId,
      status: ar.data.status,
      modified: fromApiDateTimeOptional(ar.data.modified),
      conversation: state.conversation,
      data: state,
    };
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotUpdateProcessStatus(
  processId: TenantProcess,
  typeOfEntity: number,
  entityId: number,
  status: TenantProcessStatus,
  accessToken: string,
): Promise<TenantProcessStatus | undefined> {
  try {
    const dto = mapToTenantProcessState(processId, typeOfEntity, entityId);
    dto.status = status;
    const ar = await http.put<TenantProcessStatus>(`copilot/process/state`, dto, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotGetCompanyProfileTemplate(accessToken: string): Promise<CompanyProfile | undefined> {
  try {
    const ar = await http.get<CompanyProfileDTO>(
      `copilot/tenant/companyProfile/template`,
      http.getRequestConfig(accessToken),
    );

    return mapFromCompanyProfile(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotGeneralKnowledgeCompletion(
  conversation: CopilotConversation,
  connectionId: string | null,
  accessToken: string,
  sessionId?: number,
  contextKey?: string,
  entityRef?: CopilotPanelEntityRef,
  selectedDocument?: CopilotSelectedDocument,
  instructionExternalId?: string,
  signal?: AbortSignal,
  // the entity field the user is editing while asking, with the text it holds; a field-scoped prompt only
  activeField?: CopilotActiveField,
  // the skill the Prompt binding names; a typed question sends none
  skill?: string,
  // the setId of the question card the turn answers; a typed question sends none
  answeredQuestionSetId?: string,
  // the CopilotScene member name of a list scene or the dashboard
  scene?: string,
  // the title of the session this turn creates; a field action's first turn only
  sessionTitle?: string,
  // the process the conversation is about after a process handed back (the KPI report, PLAN-070)
  activeProcess?: CopilotActiveProcessContext,
): Promise<CopilotGenericContent | undefined> {
  try {
    const dto = mapToCopilotGeneric(
      conversation,
      sessionId,
      contextKey,
      entityRef,
      selectedDocument,
      instructionExternalId,
      activeField,
      skill,
      answeredQuestionSetId,
      scene,
      sessionTitle,
      activeProcess,
    );
    const url = appendConnectionId('copilot/completions/general', connectionId);
    const ar = await http.post<CopilotGenericContentDTO>(url, dto, { ...http.getRequestConfig(accessToken), signal });
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotGeneric(ar.data);
  } catch (err) {
    if (err instanceof Error && (err.name === 'CanceledError' || err.name === 'AbortError' || err.name === 'Cancel')) {
      throw err;
    }
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotSubmitTaskApproval(
  decision: AITaskApprovalSubmitDTO,
  connectionId: string | null,
  accessToken: string,
): Promise<CopilotGenericContent | undefined> {
  try {
    const url = appendConnectionId('copilot/tasksessions/approval', connectionId);
    const ar = await http.post<CopilotGenericContentDTO>(url, decision, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotGeneric(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotUploadDocumentForIngest(
  file: File,
  sessionId: number | undefined,
  contextKey: string | undefined,
  accessToken: string,
): Promise<CopilotDocumentIngestResult | undefined> {
  try {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (sessionId) formData.append('sessionId', sessionId.toString());
    if (contextKey) formData.append('contextKey', contextKey);

    const ar = await http.post<CopilotDocumentIngestResultDTO>(
      `copilot/document/upload`,
      formData,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotDocumentIngestResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotCommitUploadedDocumentIngest(
  request: CopilotDocumentIngest,
  accessToken: string,
): Promise<CopilotDocumentIngestResult | undefined> {
  try {
    const dto = mapToCopilotDocumentIngest(request);
    const ar = await http.post<CopilotDocumentIngestResultDTO>(
      `copilot/document/upload/commit`,
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotDocumentIngestResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotReferenceLibraryDocument(
  request: CopilotDocumentIngest,
  accessToken: string,
): Promise<CopilotDocumentIngestResult | undefined> {
  try {
    const dto = mapToCopilotDocumentIngest(request);
    const ar = await http.post<CopilotDocumentIngestResultDTO>(
      `copilot/document/library`,
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotDocumentIngestResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiPrepareCopilotExternalDocumentInstructionEdit(
  externalId: string,
  accessToken: string,
): Promise<CopilotExternalDocumentInstructionEdit | undefined> {
  try {
    const request = new CopilotExternalDocumentInstructionEditRequest();
    request.externalId = externalId;
    const dto = mapToCopilotExternalDocumentInstructionEditRequest(request);
    const ar = await http.post<CopilotExternalDocumentInstructionEditDTO>(
      `copilot/document/edit/external`,
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotExternalDocumentInstructionEdit(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//
// Library document instruction edit process (tenant-scoped background edit, executed client-side).
//
// Every call here passes ignoreCustomer: the job host that drives them is mounted above the route switch, so it
// keeps polling while a partner has a customer dashboard open. These process ids only exist in the partner's own
// tenant and none of these endpoints are part of the impersonation surface, so inheriting the customer scope
// would 403 the heartbeat and let the 8 minute watchdog kill a job the partner started from its own library.
//
export async function apiGetLibraryDocumentInstructionEditProcess(
  externalId: string,
  accessToken: string,
): Promise<LibraryDocumentInstructionEditStatus | undefined> {
  try {
    const ar = await http.get<LibraryDocumentInstructionEditStatusDTO>(
      `copilot/document/edit/external/process/latest?externalId=${encodeURIComponent(externalId)}`,
      http.getRequestConfig(accessToken, undefined, true),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromLibraryDocumentInstructionEditStatus(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiStartLibraryDocumentInstructionEditProcess(
  externalId: string,
  restartMode: LibraryDocumentInstructionEditRestartMode,
  accessToken: string,
  startedSharePointVersion?: string,
  startedSharePointModified?: string,
): Promise<LibraryDocumentInstructionEditStartResult | undefined> {
  try {
    const dto = new LibraryDocumentInstructionEditStartRequestDTO();
    dto.externalId = externalId;
    dto.restartMode = restartMode;
    dto.startedSharePointVersion = startedSharePointVersion;
    dto.startedSharePointModified = startedSharePointModified;
    const ar = await http.post<LibraryDocumentInstructionEditStartResultDTO>(
      `copilot/document/edit/external/process/start`,
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromLibraryDocumentInstructionEditStartResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiClaimLibraryDocumentInstructionEditProcess(
  libraryLinkId: number,
  runId: string,
  sessionId: number | undefined,
  accessToken: string,
): Promise<LibraryDocumentInstructionEditTransitionResult | undefined> {
  try {
    const dto = new LibraryDocumentInstructionEditClaimRequestDTO();
    dto.libraryLinkId = libraryLinkId;
    dto.runId = runId;
    dto.sessionId = sessionId;
    const ar = await http.post<LibraryDocumentInstructionEditTransitionResultDTO>(
      `copilot/document/edit/external/process/claim`,
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromLibraryDocumentInstructionEditTransitionResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiHeartbeatLibraryDocumentInstructionEditProcess(
  libraryLinkId: number,
  runId: string,
  accessToken: string,
): Promise<LibraryDocumentInstructionEditTransitionResult | undefined> {
  try {
    const dto = new LibraryDocumentInstructionEditRunRequestDTO();
    dto.libraryLinkId = libraryLinkId;
    dto.runId = runId;
    const ar = await http.post<LibraryDocumentInstructionEditTransitionResultDTO>(
      `copilot/document/edit/external/process/heartbeat`,
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromLibraryDocumentInstructionEditTransitionResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiMarkSavingLibraryDocumentInstructionEditProcess(
  libraryLinkId: number,
  runId: string,
  accessToken: string,
): Promise<LibraryDocumentInstructionEditTransitionResult | undefined> {
  try {
    const dto = new LibraryDocumentInstructionEditRunRequestDTO();
    dto.libraryLinkId = libraryLinkId;
    dto.runId = runId;
    const ar = await http.post<LibraryDocumentInstructionEditTransitionResultDTO>(
      `copilot/document/edit/external/process/saving`,
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromLibraryDocumentInstructionEditTransitionResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCompleteLibraryDocumentInstructionEditProcess(
  libraryLinkId: number,
  runId: string,
  savedSharePointVersion: string,
  savedSharePointModified: string | undefined,
  accessToken: string,
): Promise<LibraryDocumentInstructionEditTransitionResult | undefined> {
  try {
    const dto = new LibraryDocumentInstructionEditCompleteRequestDTO();
    dto.libraryLinkId = libraryLinkId;
    dto.runId = runId;
    dto.savedSharePointVersion = savedSharePointVersion;
    dto.savedSharePointModified = savedSharePointModified;
    const ar = await http.post<LibraryDocumentInstructionEditTransitionResultDTO>(
      `copilot/document/edit/external/process/complete`,
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromLibraryDocumentInstructionEditTransitionResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiFailLibraryDocumentInstructionEditProcess(
  libraryLinkId: number,
  runId: string,
  error: string | undefined,
  accessToken: string,
): Promise<LibraryDocumentInstructionEditTransitionResult | undefined> {
  try {
    const dto = new LibraryDocumentInstructionEditFailRequestDTO();
    dto.libraryLinkId = libraryLinkId;
    dto.runId = runId;
    dto.error = error;
    const ar = await http.post<LibraryDocumentInstructionEditTransitionResultDTO>(
      `copilot/document/edit/external/process/fail`,
      dto,
      http.getRequestConfig(accessToken, undefined, true),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromLibraryDocumentInstructionEditTransitionResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotListTaskSessions(
  contextKey: string,
  accessToken: string,
  skip = 0,
  take = 25,
  searchText?: string,
): Promise<AITaskSessionSummary[]> {
  try {
    const params = new URLSearchParams({ contextKey, skip: skip.toString(), take: take.toString() });
    const trimmedSearchText = searchText?.trim();
    if (trimmedSearchText) params.append('q', trimmedSearchText);

    const ar = await http.get<AITaskSessionSummaryDTO[]>(
      `copilot/tasksessions?${params.toString()}`,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return [];

    return ar.data.map(mapFromAITaskSessionSummary);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotGetTaskSession(
  sessionId: number,
  accessToken: string,
): Promise<CopilotGenericContent | undefined> {
  try {
    const ar = await http.get<AITaskSessionDetailDTO>(
      `copilot/tasksessions/${sessionId}`,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromAITaskSessionDetail(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotDeleteTaskSession(sessionId: number, accessToken: string): Promise<void> {
  try {
    await http.delete(`copilot/tasksessions/${sessionId}`, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotGetLatestTaskSession(
  contextKey: string,
  accessToken: string,
): Promise<CopilotGenericContent | undefined> {
  try {
    const ar = await http.get<AITaskSessionDetailDTO>(
      `copilot/tasksessions/latest?contextKey=${encodeURIComponent(contextKey)}`,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromAITaskSessionDetail(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotStringValueCheckNew(
  conversation: CopilotConversation,
  stringValue: StringValue,
  accessToken: string,
): Promise<StringValue[] | undefined> {
  try {
    stringValue.trans = [new StringValue_Translation()];
    stringValue.transIdx = 0;

    const dto = new CopilotStringValueDTO();
    dto.conversation = mapToCopilotConversation(conversation);
    dto.stringValue = mapToStringValue(stringValue);

    const ar = await http.post<CopilotStringValueDTO>(
      `copilot/stringvalues/checknew`,
      dto,
      http.getRequestConfig(accessToken),
    );

    if (!ar || !ar.data) return undefined;
    const result = ar.data;
    let matchingStringValues: StringValue[] = [];

    if (result && result.conversation.thread.length > 1) {
      const responseItem = result.conversation.thread[result.conversation.thread.length - 1];
      const table = responseItem.contentBlocks?.find((block) => block.kind === ContentBlockKind.DataTable)?.dataTable;
      matchingStringValues = (table?.rows ?? []).map((row) => {
        const newStringValue = new StringValue();
        newStringValue.stringValueId = Number(row.stringValueId ?? 0);
        newStringValue.stringTypeId = stringValue.stringTypeId;
        newStringValue.stringValueKey = Number(row.rank ?? 0);
        newStringValue.value = String(row.value ?? '');

        return newStringValue;
      });
    }

    return matchingStringValues.sort((a, b) => sortOnNumber(a.stringValueKey, b.stringValueKey));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotProcessingActivityCompliance(
  accessToken: string,
  copilotProcessingActivity: CopilotProcessingActivity,
  privacyFramework: PrivacyFramework,
): Promise<CopilotProcessingActivity | undefined> {
  try {
    const dto = mapToCopilotProcessingActivity(copilotProcessingActivity, privacyFramework);

    const ar = await http.post<CopilotProcessingActivityDTO>(
      `copilot/processingActivity/compliance`,
      dto,
      http.getRequestConfig(accessToken),
    );
    if (!ar || !ar.data) return undefined;

    return mapFromCopilotProcessingActivity(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCopilotUploadFileToStorage(file: File, accessToken: string): Promise<string | undefined> {
  try {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const ar = await http.post(`copilot/storage/upload`, formData, http.getRequestConfig(accessToken));

    if (!ar?.data) return undefined;

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
